import React, { useState, useEffect } from "react";

import Typography from "@mui/material/Typography";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import AccountMenu from "./components/AccountMenu";
import PricingPlaneDialog from "../PricingPlansDialog";
import { usersService } from "./services";
import calculateUsedCreditsInPercentage from "../../../utils/calculateCreditUsage";
import { useUserState } from "../../../store";

interface Props {
  pageName: string;
  onMobileMenuClick?: () => void;
  showLogo?: boolean;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
interface CreditSubscription {
  status: string;
  pricing_cycle: string;
  start_date: string;
  end_date: string;
  available_credits: number;
  pricing_plan: {
    id: string;
    name: string;
    description: string;
    created_at: string;
    credits_per_month: number;
  };
}

interface CreditOrganization {
  id: string;
  name: string;
  created_at: string;
  owner_id: string;
  subscription: CreditSubscription;
}

interface CreditUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  organization: CreditOrganization;
  roles: string[];
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: "100%",
  marginLeft: 0,
  position: "relative",
}));

export default function Headerbar({
  pageName,
  onMobileMenuClick,
  showLogo = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openPricingPlanDialog, setOpenPricingPlanDialog] = useState(false);
  const { updateUser } = useUserState();

  const handleOpenPricingPlanDialog = () => {
    setOpenPricingPlanDialog(!openPricingPlanDialog);
  };

  const { data, isSuccess } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  useEffect(() => {
    if (isSuccess && data) {
      updateUser(data);
    }
  }, [isSuccess, data, updateUser]);

  const creditData = data as CreditUser | undefined;

  const availableCredits =
    creditData?.organization.subscription?.available_credits || 0;

  const totalCreditsPerMonth =
    creditData?.organization.subscription?.pricing_plan?.credits_per_month || 0;

  const percentageUsed =
    totalCreditsPerMonth > 0
      ? calculateUsedCreditsInPercentage(totalCreditsPerMonth, availableCredits)
      : 0;

  const formatCredits = (credits: number) => {
    if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}k`;
    }
    return credits.toString();
  };

  const remainingCredits = formatCredits(availableCredits);

  const showCreditUsage = totalCreditsPerMonth > 0;

  return (
    <AppBar
      position="absolute"
      sx={{
        backgroundColor: "#ffffff",
        color: "#23272E",
        borderRight: "1px solid #e9ecef",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <Toolbar
        sx={{
          pr: { xs: 1.5, sm: 2, md: 3 },
          pl: { xs: 1.5, sm: 2, md: 3 },
          minHeight: { xs: "68px", sm: "64px", md: "64px" },
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "space-between", md: "flex-start" },
          gap: { xs: 1, sm: 2 },
        }}
      >
        {/* Mobile hamburger button */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open mobile menu"
            onClick={onMobileMenuClick}
            sx={{
              color: "#1976d2",
              backgroundColor: "#f5f7fa",
              border: "1px solid #e9ecef",
              width: { xs: 44, sm: 48 },
              height: { xs: 44, sm: 48 },
              "&:hover": {
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
              },
            }}
          >
            <img
              src="/logo.png"
              alt="Podcast Assistant Logo"
              style={{
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          </IconButton>
        )}

        {/* Page title or Logo with text */}
        {showLogo ? (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0, flexGrow: 1 }}
          >
            <img
              src="/logo.png"
              alt="Podcast Assistant Logo"
              style={{
                width: "42px",
                height: "42px",
                display: "block",
              }}
            />
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              noWrap
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                color: "#23272E",
                lineHeight: 1.3,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: "0.02em",
                mt: 0.5,
              }}
            >
              Podcast Assistant
            </Typography>
          </Box>
        ) : (
          <Typography
            component="h1"
            variant="h5"
            color="inherit"
            noWrap
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              color: "#23272E",
              marginLeft: { xs: 1, sm: 2 },
              lineHeight: 1.2,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {pageName}
          </Typography>
        )}

        {/* Desktop features - hidden on mobile */}
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Time indicator */}
            {showCreditUsage && (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                }}
              >
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={40}
                    sx={{
                      color: "#e9ecef",
                    }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={percentageUsed}
                    size={40}
                    sx={{
                      color: "#1976d2",
                      position: "absolute",
                      left: 0,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Tooltip
                    title={`Remaining Credits: ${availableCredits} credits`}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: "#23272E",
                        fontSize: "11px",
                        fontWeight: 600,
                        cursor: "help",
                      }}
                    >
                      {remainingCredits}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Upgrade button */}
            <Button
              onClick={handleOpenPricingPlanDialog}
              variant="contained"
              size="small"
              sx={{
                background: "linear-gradient(135deg, #1976d2, #1565c0)",
                borderRadius: 20,
                px: { xs: 2, sm: 3 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 12, sm: 13 },
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                border: "none",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  background: "linear-gradient(135deg, #1565c0, #0d47a1)",
                  boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                  transition: "left 0.5s",
                },
                "&:hover::before": {
                  left: "100%",
                },
              }}
              startIcon={
                <BoltIcon
                  sx={{
                    color: "#ffd54f",
                    fontSize: { xs: 16, sm: 18 },
                    filter: "drop-shadow(0 0 4px rgba(255, 213, 79, 0.6))",
                  }}
                />
              }
            >
              Upgrade
            </Button>
          </Box>
        )}

        {/* Account menu */}
        <AccountMenu userData={creditData} />
      </Toolbar>
      <PricingPlaneDialog
        handleOpenPricingPlanDialog={handleOpenPricingPlanDialog}
        open={openPricingPlanDialog}
      />
    </AppBar>
  );
}
